<script setup lang="ts">
import { format } from 'date-fns'
import { NodeTypes } from '@marvr/storyblok-rich-text-types'
import { useMask } from 'core/services/mask'

const DEFAULT_VALUES = {
  INSTITUTION_ID: 2,
  INSTITUTION_NAME_FROM_URL: 'ceska-sporitelna',
  TYPE_FROM_URL: 'valuty',
  DATE_FORMAT: 'yyyyMMdd',
  CZECH_CURRENCY: 'CZK',
}

const route = useRoute()
const company = route.params?.company || DEFAULT_VALUES.INSTITUTION_NAME_FROM_URL
const type = route.params?.type === DEFAULT_VALUES.TYPE_FROM_URL ? 'val' : 'dev'
const todayDate = new Date()
const formTableControls = ref<any>({})
const formRates = ref<any>({})
const { moneyAmount } = useMask()

const sbApi = useStoryblokApiV2()
const formFields: StoryBlokForm = await sbApi.getForm('/cz/rates')
const data: StoryblokPage = await sbApi.getPage('/kurzy')
useStoryblokMeta(data)
const pageCustomData = data.customPage[0] as RatesPageData

const institutionCodeFromUrl = formFields.fields.find(field => field.name === 'institution')?.options
  ?.find(option => useURLSafeString(option.label) === company)?.code || DEFAULT_VALUES.INSTITUTION_ID
const formatedDate = computed(() => format(new Date(formTableControls.value?.values?.date || todayDate), DEFAULT_VALUES.DATE_FORMAT))

const formInitialValues = {
  institution: institutionCodeFromUrl,
  date: todayDate,
  type,
}

const amountClientIndex = formFields.fields.findIndex(field => field.name === 'amountClient')
const amountBankIndex = formFields.fields.findIndex(field => field.name === 'amountBank')

const { data: rates, pending } = await useFetch<Rates>(() => `https://data.kurzy.cz/json/meny/b[${
  formTableControls.value?.values?.institution || institutionCodeFromUrl
}]den[${
  formatedDate.value
}].json`)

const ratesData = computed(() => Object.entries(rates.value!.kurzy).map(([code, currencyData]) => ({
  code,
  currencyData,
  country: pageCustomData.countryNames.find(country => country.id === code),
})))

const tableRows = computed(() => ratesData.value.map((currency) => {
  return {
    items: [
      { country: currency.country },
      useCreateRichtextRootNode([{ text: currency.code, type: NodeTypes.TEXT }]),
      useCreateRichtextRootNode([{ text: currency.currencyData.jednotka, type: NodeTypes.TEXT }]),
      useCreateRichtextRootNode([{
        text: currency.currencyData[`${formTableControls.value?.values?.type}_nakup` as keyof typeof currency.currencyData] || 0,
        type: NodeTypes.TEXT,
        marks: [{ type: NodeTypes.BOLD }],
      }]),
      useCreateRichtextRootNode([{
        text: currency.currencyData[`${formTableControls.value?.values?.type}_prodej` as keyof typeof currency.currencyData] || 0,
        type: NodeTypes.TEXT,
        marks: [{ type: NodeTypes.BOLD }],
      }]),
    ],
  }
}))

const czechia = pageCustomData.countryNames.find(country => country.id === DEFAULT_VALUES.CZECH_CURRENCY)
const dropdownsData = computed(() => {
  return [
    { code: czechia?.id, label: `${czechia?.id} - ${czechia?.text}`, img: czechia?.image },
    ...ratesData.value.map(currency => ({
      code: currency.code,
      label: `${currency.code} - ${currency.country?.text}`,
      img: currency.country?.image,
    })),
  ]
})

function setCzechCurrency(dropdownToChange: string) {
  formRates.value.setFieldValue(dropdownToChange, DEFAULT_VALUES.CZECH_CURRENCY)
}

function setTitles(name: string) {
  if (name === 'amountClient') {
    formFields.fields[amountClientIndex].label = pageCustomData.amountClientTitle1
    formFields.fields[amountBankIndex].label = pageCustomData.amountBankTitle1
  }
  else {
    formFields.fields[amountClientIndex].label = pageCustomData.amountClientTitle2
    formFields.fields[amountBankIndex].label = pageCustomData.amountBankTitle2
  }
}

function calculate(key: string, name: string) {
  const finalAmount = ref(0)
  const userSelectedCurrencyObject = formRates.value.values.currenciesClient === DEFAULT_VALUES.CZECH_CURRENCY
    ? {
        code: formRates.value.values.currenciesBank,
        name: 'currenciesBank',
      }
    : {
        code: formRates.value.values.currenciesClient,
        name: 'currenciesClient',
      }

  const currency = rates.value!.kurzy[userSelectedCurrencyObject.code]

  const currencyValue = currency[`${formTableControls.value?.values?.type}_${
    userSelectedCurrencyObject.name === 'currenciesBank' ? 'prodej' : 'nakup'
  }` as keyof typeof currency] as number || 0

  if (name === 'amountClient') {
    finalAmount.value = userSelectedCurrencyObject.name === 'currenciesBank'
      ? (formRates.value.values[name] / currencyValue) * currency.jednotka
      : (formRates.value.values[name] * currencyValue) / currency.jednotka
  }
  else {
    finalAmount.value = userSelectedCurrencyObject.name === 'currenciesBank'
      ? (formRates.value.values[name] * currencyValue) / currency.jednotka
      : (formRates.value.values[name] / currencyValue) * currency.jednotka
  }

  formRates.value.values[name === 'amountClient' ? 'amountBank' : 'amountClient'] = finalAmount.value.toFixed(2)
}
</script>

<template>
  <div class="bg-gray-light">
    <div class="content-container pt-6 pb-9 flex flex-col gap-3">
      <AppBreadcrumbs class="text-gray-dark" />
      <h1>
        {{ pageCustomData.title }} - {{ rates?.banka }} - {{
          formFields?.fields.find(field => field.name === 'type')?.items
            ?.find(item => item.value === formTableControls.values?.type)?.text
        }} - pro den {{ rates?.denc }}
      </h1>
    </div>
  </div>
  <div class="content-container">
    <div class="max-w-780px mx-auto py-17.5 flex flex-col gap-17.5">
      <!-- Table with table controls -->
      <div class="flex flex-col gap-6">
        <FormSimple ref="formTableControls" :fields="formFields?.fields" :initial-values="formInitialValues" class="flex gap-6 <md:flex-col">
          <FormFieldDropdown name="institution" class="md:max-w-275px" />
          <FormFieldDatepicker
            name="date"
            class="md:max-w-275px"
            :range-end-date="todayDate"
            :max-date="todayDate"
            :start-date="todayDate"
            transitions
          />
          <div class="flex items-end mb-2">
            <FormFieldRadioGroup name="type" class="flex gap-6" />
          </div>
        </FormSimple>
        <div>
          <ComponentTable
            v-if="pending || rates?.den === formatedDate"
            type="normal"
            :headers="[
              { name: pageCustomData.hrCountry },
              { name: pageCustomData.hrCode },
              { name: pageCustomData.hrAmount },
              { name: pageCustomData[`hr${formTableControls?.values?.type}Buy` as keyof typeof pageCustomData] as string },
              { name: pageCustomData[`hr${formTableControls?.values?.type}Sell` as keyof typeof pageCustomData] as string },
            ]"
            :rows="tableRows"
            :footer-text="pageCustomData.tableFooterText"
          >
            <template #default="{ item }: { item: { text: object, country?: { id: string, image: StoryBlokImage, name: string } } }">
              <div v-if="item?.country" :key="item.country?.id" class="flex items-center gap-2 min-w-110px">
                <StoryblokImage width="16" :src="item.country?.image" />
                {{ item.country?.name }}
              </div>
            </template>
          </ComponentTable>
          <div v-else class="bg-gray-light rounded-xl p-5">
            <b class="flex gap-1 justify-center">
              {{ pageCustomData.oldDataText }}
              <StoryblokLink
                :key="rates?.den"
                class="text-primary underline cursor-pointer"
                @click.prevent="formTableControls.setFieldValue('date', new Date(`${rates!.den.substring(0, 4)}-${rates!.den.substring(4, 6)}-${rates!.den.substring(6, 8)}`))"
              >
                {{ rates?.denc }}
              </StoryblokLink>
            </b>
          </div>
        </div>
      </div>

      <!-- Currency conversion -->
      <FormSimple ref="formRates" :fields="formFields?.fields" class="p-6 flex flex-col gap-6 rounded-xl border-1 border-gray bg-gray-light">
        <h3>{{ pageCustomData.titleRates }}</h3>
        <div class="flex gap-6 <md:flex-col">
          <div class="flex flex-col gap-6 w-full">
            <FormFieldText
              class="w-full"
              name="amountClient"
              :mask="moneyAmount"
              @focus="setTitles"
              @keyup="calculate"
            />
            <FormFieldDropdown
              name="currenciesClient"
              :options="dropdownsData"
              @select="setCzechCurrency('currenciesBank')"
            >
              <template #singlelabel="{ value }: { value: CountryImage }">
                <span
                  :key="value.code"
                  class="flex gap-2 items-center h-full max-w-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5 pr-8 box-border rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3.5"
                >
                  <StoryblokImage width="16" :src="value.img" />
                  <span class="overflow-ellipsis overflow-hidden block whitespace-nowrap max-w-full">{{ value.label }}</span>
                </span>
              </template>
              <template #option="{ option }: { option: CountryImage }">
                <span :key="option.code" class="flex gap-2 items-center">
                  <StoryblokImage width="16" :src="option.img" />
                  {{ option.label }}
                </span>
              </template>
            </FormFieldDropdown>
          </div>
          <div class="flex flex-col gap-6 w-full">
            <FormFieldText
              class="w-full"
              name="amountBank"
              :mask="moneyAmount"
              @focus="setTitles"
              @keyup="calculate"
            />
            <FormFieldDropdown
              name="currenciesBank"
              :options="dropdownsData"
              @select="setCzechCurrency('currenciesClient')"
            >
              <template #singlelabel="{ value }: { value: CountryImage }">
                <span
                  :key="value.code"
                  class="flex gap-2 items-center h-full max-w-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5 pr-8 box-border rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3.5"
                >
                  <StoryblokImage width="16" :src="value.img" />
                  <span class="overflow-ellipsis overflow-hidden block whitespace-nowrap max-w-full">{{ value.label }}</span>
                </span>
              </template>
              <template #option="{ option }: { option: CountryImage }">
                <span :key="option.code" class="flex gap-2 items-center">
                  <StoryblokImage width="16" :src="option.img" />
                  {{ option.label }}
                </span>
              </template>
            </FormFieldDropdown>
          </div>
        </div>
      </FormSimple>
    </div>
  </div>

  <!-- Page blocks -->
  <div v-for="pageBlock in data?.body" :key="pageBlock._uid">
    <ComponentPageBlock v-bind="pageBlock" />
  </div>
</template>
